import React, { useState } from 'react';
import { getAppointmentDossier } from '../../calendar/appointmentDossiers';
import {
  getAppointmentInsurance,
  type AppointmentInsuranceDossier,
  type FamilyOverviewMember,
  type InsuranceEstimate,
  type OpportunityItem,
  type OpportunityPriority,
} from '../../calendar/appointmentInsuranceEstimate';
import { PROVIDERS, STATUS } from '../../calendar/mockData';
import type { Appointment, AppointmentDossier } from '../../calendar/types';
import { cellTone, TONE_STYLES } from '../../calendar/utils';
import { formatTimeRange } from '../../calendar/timeUtils';
import { cn } from '../../lib/cn';
import { Avatar } from '../Avatar/Avatar';
import { Button } from '../Button/Button';
import { Checkbox } from '../Checkbox/Checkbox';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Modal } from '../Modal/Modal';
import { Pill } from '../Pill/Pill';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';
import { InvAlignersCard } from '../Invisalign/InvAlignersCard';

const cardLabelClass = 'font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.12em]';

const money = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: Number.isInteger(n) ? 0 : 2 })}`;

const EST_TONE: Record<string, string> = {
  ai: 'var(--ai-3)',
  gold: 'var(--gold)',
  success: 'var(--success)',
  ink: 'var(--ink-0)',
};

/* ── Print summary ─────────────────────────────────────────────────────────
 * "Print Summary" builds a clean, standalone one-page estimate sheet and prints
 * that — rather than window.print() of the on-screen modal, which prints the
 * cramped, clipped live layout. Opens a fresh window so it doesn't depend on
 * global print CSS. Apps can override entirely via the `onPrintSummary` prop.  */

interface PrintClinicalAlerts {
  allergies: string[];
  conditions: string[];
  medications: string[];
}

interface PrintSummaryData {
  patient: string;
  demographics: string;
  timeLabel: string;
  provider: string;
  operatory: string;
  verified?: string | undefined;
  estimate?: InsuranceEstimate | undefined;
  opportunities: OpportunityItem[];
  alerts: PrintClinicalAlerts;
  /** Which optional sections to include, chosen in the pre-print dialog. */
  include: { opportunities: boolean; alerts: boolean };
}

function escapeHtml(s: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return s.replace(/[&<>"']/g, (c) => map[c] ?? c);
}

function buildPrintSummaryDoc(d: PrintSummaryData): string {
  const e = d.estimate;
  const p = e?.plan;
  const rows = (e?.procedures ?? [])
    .map(
      (proc) => `<tr>
        <td class="l">${escapeHtml(proc.code)} — ${escapeHtml(proc.description)}</td>
        <td class="n">${money(proc.officeFee)}</td>
        <td class="n cr">${proc.insuranceEst > 0 ? `−${money(proc.insuranceEst)}` : money(0)}</td>
        <td class="n b">${money(proc.patientEst)}</td>
      </tr>`,
    )
    .join('');
  const oppHtml =
    d.include.opportunities && d.opportunities.length > 0
      ? `<div class="kicker" style="margin-top:16px">Opportunities</div>${d.opportunities
          .map(
            (o) => `<div class="opp">
        <div class="opp-h"><span class="opp-t">${escapeHtml(o.title)}</span>${o.priority ? `<span class="badge">${escapeHtml(o.priority)}</span>` : ''}</div>
        <div class="muted">${escapeHtml(o.description)}${o.detail ? ` · ${escapeHtml(o.detail)}` : ''}</div>
        ${o.estValue ? `<div class="opp-est">${escapeHtml(o.estLabel ?? '')}: <b>${escapeHtml(o.estValue)}</b></div>` : ''}
      </div>`,
          )
          .join('')}`
      : '';

  const ag = (label: string, items: string[]) =>
    `<div class="ag"><div class="kicker">${label} (${String(items.length)})</div><div>${
      items.length > 0 ? items.map(escapeHtml).join(', ') : 'None on file'
    }</div></div>`;
  const alertsHtml = d.include.alerts
    ? `<div class="kicker" style="margin-top:16px">Clinical alerts</div><div class="alerts">${ag(
        'Allergies',
        d.alerts.allergies,
      )}${ag('Conditions', d.alerts.conditions)}${ag('Medications', d.alerts.medications)}</div>`
    : '';

  const printedAt = new Date().toLocaleString();

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Insurance Estimate — ${escapeHtml(d.patient)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 32px 36px; color: #14203a; font: 13px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
  h1 { margin: 0 0 2px; font-size: 20px; }
  .muted { color: #64748b; }
  .kicker { text-transform: uppercase; letter-spacing: .08em; font-size: 10px; font-weight: 700; color: #64748b; }
  .row { display: flex; flex-wrap: wrap; gap: 24px; }
  .row > div { min-width: 120px; }
  .rule { border: none; border-top: 1px solid #e3e8f0; margin: 16px 0; }
  .hero { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 28px; background: #f5f8ff; border: 1px solid #e3e8f0; border-radius: 10px; padding: 14px 18px; margin: 4px 0 8px; }
  .big { font-size: 30px; font-weight: 800; letter-spacing: -.02em; }
  .teal { color: #0f8a86; }
  .green { color: #12855f; }
  .op { font-size: 20px; color: #94a3b8; padding: 0 2px; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  th, td { padding: 7px 6px; }
  th { text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: #64748b; border-bottom: 1px solid #e3e8f0; }
  th.n, td.n { text-align: right; font-variant-numeric: tabular-nums; }
  td { border-bottom: 1px solid #eef2f8; }
  td.cr { color: #12855f; }
  td.b { font-weight: 700; }
  tr.totals td { border-top: 2px solid #cbd5e1; border-bottom: none; font-weight: 700; }
  .tiles { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 12px; }
  .tile { border: 1px solid #e3e8f0; border-radius: 8px; padding: 9px 11px; }
  .tile .v { font-size: 16px; font-weight: 700; margin-top: 2px; }
  .tile .s { font-size: 11px; color: #64748b; margin-top: 1px; }
  .disc { color: #94a3b8; font-size: 11px; margin-top: 14px; }
  .foot { color: #94a3b8; font-size: 10px; margin-top: 20px; }
  .opp { border: 1px solid #e3e8f0; border-radius: 8px; padding: 9px 11px; margin-top: 8px; }
  .opp-h { display: flex; align-items: center; gap: 8px; }
  .opp-t { font-weight: 700; }
  .badge { font-size: 10px; font-weight: 700; color: #12855f; background: #e8f5ee; border-radius: 5px; padding: 1px 6px; }
  .opp-est { font-size: 12px; margin-top: 2px; }
  .opp-m { font-style: italic; color: #475569; font-size: 12px; margin-top: 3px; }
  .alerts { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin-top: 6px; }
  .ag { border: 1px solid #eef2f8; border-radius: 8px; padding: 8px 10px; font-size: 12px; }
  @media print { body { padding: 0; } .opp, .ag { break-inside: avoid; } @page { margin: 14mm; } }
</style>
</head>
<body>
  <div class="kicker">Insurance Estimate Summary</div>
  <h1>${escapeHtml(d.patient)}</h1>
  <div class="muted">${escapeHtml(d.demographics)}</div>
  <hr class="rule" />
  <div class="row">
    ${e?.carrier ? `<div><div class="kicker">Insurance</div><div>${escapeHtml(e.carrier)}</div></div>` : ''}
    <div><div class="kicker">Appointment</div><div>${escapeHtml(d.timeLabel)}</div></div>
    <div><div class="kicker">Provider</div><div>${escapeHtml(d.provider)}</div></div>
    <div><div class="kicker">Operatory</div><div>${escapeHtml(d.operatory)}</div></div>
    ${d.verified ? `<div><div class="kicker">Eligibility verified</div><div>${escapeHtml(d.verified)}</div></div>` : ''}
  </div>
  <hr class="rule" />
  ${
    e
      ? `<div class="hero">
    <div>
      <div class="kicker">Patient estimated today</div>
      <div class="big teal">${money(e.patientToday)}</div>
      <div class="muted">${escapeHtml(e.patientNote ?? 'You pay today (est.)')}</div>
    </div>
    ${
      e.officeFee !== undefined && e.insuranceEst !== undefined && e.patientEst !== undefined
        ? `<div class="row" style="align-items:flex-end;gap:14px">
      <div><div class="kicker">Office Fee</div><div class="big" style="font-size:22px">${money(e.officeFee)}</div></div>
      <div class="op">−</div>
      <div><div class="kicker">Insurance Est.</div><div class="big green" style="font-size:22px">${money(e.insuranceEst)}</div></div>
      <div class="op">=</div>
      <div><div class="kicker">Patient Est.</div><div class="big teal" style="font-size:22px">${money(e.patientEst)}</div></div>
    </div>`
        : ''
    }
  </div>`
      : ''
  }
  ${
    e && rows
      ? `<div class="kicker" style="margin-top:14px">Today's procedures</div>
  <table>
    <thead>
      <tr><th class="l"></th><th class="n">Office Fee</th><th class="n">Insurance Est.</th><th class="n">Patient Est.</th></tr>
    </thead>
    <tbody>
      ${rows}
      ${
        e.officeFee !== undefined && e.insuranceEst !== undefined && e.patientEst !== undefined
          ? `<tr class="totals">
        <td>Totals</td>
        <td class="n">${money(e.officeFee)}</td>
        <td class="n cr">−${money(e.insuranceEst)}</td>
        <td class="n b">${money(e.patientEst)}</td>
      </tr>`
          : ''
      }
    </tbody>
  </table>`
      : ''
  }
  ${
    p
      ? `<div class="tiles">
    <div class="tile"><div class="kicker">Annual Max</div><div class="v">${money(p.annualMax)}</div><div class="s">${money(p.annualRemaining)} remaining</div></div>
    <div class="tile"><div class="kicker">Deductible</div><div class="v">${money(p.deductible)}</div><div class="s">${p.deductibleMet ? 'Met' : 'Not met'}</div></div>
    <div class="tile"><div class="kicker">Ortho Lifetime Max</div><div class="v">${money(p.orthoMax)}</div><div class="s">${money(p.orthoRemaining)} remaining</div></div>
    <div class="tile"><div class="kicker">Plan Type</div><div class="v">${escapeHtml(p.planType)}</div></div>
    <div class="tile"><div class="kicker">Effective</div><div class="v">${escapeHtml(p.effectiveFrom)}</div><div class="s">to ${escapeHtml(p.effectiveTo)}</div></div>
  </div>`
      : ''
  }
  <div class="disc">Estimates are not a guarantee of payment. Final insurance payment may vary.</div>
  ${oppHtml}
  ${alertsHtml}
  <div class="foot">Printed ${escapeHtml(printedAt)}</div>
</body>
</html>`;
}

function printEstimateSummary(d: PrintSummaryData): void {
  if (typeof window === 'undefined') return;
  const url = URL.createObjectURL(new Blob([buildPrintSummaryDoc(d)], { type: 'text/html' }));
  const w = window.open(url, '_blank', 'width=860,height=1100');
  if (!w) {
    URL.revokeObjectURL(url);
    window.print();
    return;
  }
  window.setTimeout(() => {
    w.focus();
    w.print();
    URL.revokeObjectURL(url);
  }, 500);
}

function Chip({ icon, accent, size = 30 }: { icon: IconKind; accent: string; size?: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-[9px]"
      style={{
        width: size,
        height: size,
        background: `color-mix(in srgb, ${accent} 14%, transparent)`,
        color: accent,
      }}
    >
      <Icon kind={icon} size={size * 0.5} stroke={1.9} />
    </span>
  );
}

function SectionCard({
  icon,
  title,
  accent = 'var(--info)',
  right,
  children,
  bodyPad = 'px-[18px] pb-[18px] pt-1',
  tint,
  className,
}: {
  icon: IconKind;
  title: string;
  accent?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  bodyPad?: string;
  tint?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] shadow-[var(--sh-1)]',
        className,
      )}
      style={{ background: tint ?? 'var(--cream-1)', borderTopWidth: 3, borderTopColor: accent }}
    >
      <div className="flex items-center gap-2 px-[18px] pb-3 pt-4">
        <Icon kind={icon} size={14} stroke={2} color={accent} />
        <span className={cn(cardLabelClass)} style={{ color: accent }}>
          {title}
        </span>
        {right ? <span className="ml-auto">{right}</span> : null}
      </div>
      <div className={bodyPad}>{children}</div>
    </div>
  );
}

export interface AppointmentDetailProps {
  appointment: Appointment | null;
  onClose: () => void;
  /** Server-backed dossier; falls back to the mock dossier when omitted. */
  dossier?: AppointmentDossier;
  /** Server-backed insurance / opportunities / family; falls back to the mock when omitted. */
  insurance?: AppointmentInsuranceDossier;
  providerName?: string;
  /** Patient photo; the initials avatar shows when omitted or if it fails to load. */
  patientAvatarUrl?: string;
  patientInitials?: string;
  patientProfileHref?: string;
  onViewProfile?: () => void;
  onChangeStatus?: (status: string) => void;
  onChangeOperatory?: (operatory: string) => void;
  onResendKioskCode?: () => void;
  onCheckEligibility?: () => void;
  eligibilityLastChecked?: string;
  eligibilityStale?: boolean;
  /** Only a verified (or unspecified) check earns the "Eligibility verified" badge. */
  eligibilityStatus?: 'verified' | 'failed' | 'pending' | 'unknown';
  /** Canonical status value to mark as current in the change-status menu. */
  currentStatus?: string;
  /** Disables the matching header action while its request is in flight. */
  busyAction?: 'status' | 'operatory' | 'kiosk' | 'eligibility' | null;
  operatories?: string[];
  /** Print the estimate summary. Defaults to window.print() when omitted. */
  onPrintSummary?: () => void;
}

const STATUS_ACTIONS: { value: string; label: string; icon: IconKind }[] = [
  { value: 'CONFIRMED', label: 'Confirmed', icon: 'check' },
  { value: 'ARRIVED', label: 'Arrived / waiting', icon: 'arrival' },
  { value: 'SEATED', label: 'Seated', icon: 'chair' },
  { value: 'TREATMENT_DONE', label: 'Treatment done', icon: 'check-circ' },
  { value: 'CHECKED_OUT', label: 'Checked out', icon: 'logout' },
  { value: 'NO_SHOW', label: 'No-show', icon: 'x' },
  { value: 'CANCELLED', label: 'Cancelled', icon: 'minus' },
  { value: 'RESCHEDULED', label: 'Rescheduled', icon: 'refresh' },
];

export function AppointmentDetail({
  appointment: appt,
  onClose,
  dossier: dossierProp,
  insurance,
  providerName,
  patientAvatarUrl,
  patientInitials,
  patientProfileHref,
  onViewProfile,
  onChangeStatus,
  onChangeOperatory,
  onResendKioskCode,
  onCheckEligibility,
  eligibilityLastChecked,
  eligibilityStale = false,
  eligibilityStatus,
  currentStatus,
  busyAction = null,
  operatories = [],
  onPrintSummary,
}: AppointmentDetailProps) {
  const [openMenu, setOpenMenu] = useState<'status' | 'operatory' | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [inclOpportunities, setInclOpportunities] = useState(false);
  const [inclAlerts, setInclAlerts] = useState(false);

  if (!appt) return null;

  const dossier = dossierProp ?? getAppointmentDossier(appt);
  const ins = insurance ?? getAppointmentInsurance(appt);
  const provName =
    providerName ?? PROVIDERS.find((p) => p.id === appt.providerId)?.name ?? 'Unassigned';
  const operatoryOptions = operatories.length > 0 ? operatories : [dossier.operatory];
  const st = STATUS[appt.status] ?? { label: 'Scheduled', tone: 'neutral' as const };
  const tone = cellTone(appt.flags);
  const tn = TONE_STYLES[tone];
  const timeLabel = `${formatTimeRange(appt.hour, appt.hour + appt.duration)} · ${String(
    Math.round(appt.duration * 60),
  )}m`;
  const lastName = appt.patient.split(' ').slice(-1)[0] ?? appt.patient;
  const firstNames = appt.patient.split(' ').slice(0, -1).join(' ');

  const eligibilityVerified = eligibilityStatus === undefined || eligibilityStatus === 'verified';
  const verified = eligibilityVerified
    ? (eligibilityLastChecked ?? ins.estimate?.verifiedAt)
    : undefined;
  const activeStatus = currentStatus ?? appt.status;

  return (
    <>
    <ViewportOverlay open onClose={onClose}>
      <div
        onClick={(ev) => {
          ev.stopPropagation();
        }}
        className="cal-modal flex max-h-[92vh] w-[min(1480px,calc(100vw-48px))] flex-col overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)]"
        style={{ boxShadow: '0 32px 80px rgba(20,30,50,0.24), 0 8px 24px rgba(20,30,50,0.12)' }}
      >
        {/* ── Header (identity left · wired actions top-right) ─────────── */}
        <div className="relative z-40 border-b border-[var(--cream-2)] bg-[var(--cream-1)] px-7 pb-[22px] pt-[26px]">
          <span
            aria-hidden
            className="absolute left-0 right-0 top-0 h-[3px] bg-[var(--grad-spectrum)]"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute -right-10 -top-[90px] h-[200px] w-[320px] blur-[20px]"
            style={{
              background:
                'radial-gradient(closest-side, color-mix(in srgb, var(--ai-3) 16%, transparent), transparent)',
            }}
          />
          <div className="flex items-start justify-between gap-5">
            {/* Identity */}
            <div className="min-w-0">
              <div className="mb-3 flex items-center gap-2">
                <Icon kind="sparkle" size={13} stroke={2} color={tn.fg} />
                <span
                  className="font-[var(--font-mono)] text-[11px] font-bold uppercase tracking-[0.16em]"
                  style={{ color: tn.fg }}
                >
                  {st.label} ·{' '}
                  {tone === 'green' ? 'Ready' : tone === 'yellow' ? 'Needs attention' : 'Critical'}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <Avatar
                  name={appt.patient}
                  src={patientAvatarUrl}
                  initials={patientInitials}
                  hue="var(--ai-3)"
                  size={56}
                />
                <h2 className="m-0 font-[var(--font-display)] text-[clamp(28px,4vw,38px)] font-bold leading-[1.04] tracking-[-0.04em] text-[var(--ink-0)]">
                  {firstNames ? `${firstNames} ` : null}
                  <span className="cal-spectrum">{lastName}</span>
                </h2>
              </div>
              <div className="mt-3.5 flex flex-wrap items-center gap-2.5">
                {appt.flags.includes('new_pt') ? (
                  <Pill tone="gold">
                    <Icon kind="sparkle" size={9} stroke={2} /> New patient
                  </Pill>
                ) : null}
                {appt.flags.includes('vip') ? (
                  <Pill tone="ai">
                    <Icon kind="crown" size={9} stroke={2} /> VIP referrer
                  </Pill>
                ) : null}
                <span className="text-[13.5px] text-[var(--ink-2)]">{dossier.demographics}</span>
                {patientProfileHref ? (
                  <a
                    href={patientProfileHref}
                    onClick={(ev) => {
                      if (onViewProfile) {
                        ev.preventDefault();
                        onViewProfile();
                      }
                    }}
                    className="text-[13px] font-semibold text-[var(--ai-3)] no-underline"
                  >
                    View full profile →
                  </a>
                ) : null}
              </div>
            </div>

            {/* Close + wired actions — each control shows its current state */}
            <div className="flex shrink-0 flex-col items-end gap-2.5">
              <IconButton icon="x" size={34} onClick={onClose} label="Close" />
              <div className="flex flex-wrap items-start justify-end gap-2">
                {/* Status — shows current status; click to change */}
                <div className="flex flex-col items-stretch gap-1">
                  <div className="relative">
                    <Button
                      variant="secondary"
                      size="sm"
                      iconLeft="activity"
                      loading={busyAction === 'status'}
                      onClick={() => {
                        setOpenMenu((m) => (m === 'status' ? null : 'status'));
                      }}
                    >
                      {st.label}
                      <Icon kind="down" size={12} stroke={2} />
                    </Button>
                    {openMenu === 'status' ? (
                      <div className="absolute right-0 top-full z-20 mt-1 min-w-[210px] overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] py-1 shadow-[var(--sh-3)]">
                        {STATUS_ACTIONS.map((s) => (
                          <button
                            key={s.value}
                            type="button"
                            onClick={() => {
                              onChangeStatus?.(s.value);
                              setOpenMenu(null);
                            }}
                            className={cn(
                              'flex w-full items-center gap-2 px-3.5 py-2 text-left text-[13px] text-[var(--ink-1)] hover:bg-[var(--cream-1)]',
                              s.value === activeStatus && 'text-[var(--ai-3)]',
                            )}
                          >
                            <Icon kind={s.icon} size={14} stroke={2} />
                            {s.label}
                            {s.value === activeStatus ? (
                              <span className="ml-auto text-[11px] text-[var(--ink-3)]">current</span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <span className={cn(cardLabelClass, 'text-center text-[var(--ink-4)]')}>Status</span>
                </div>

                {/* Operatory — shows current operatory; click to change */}
                <div className="flex flex-col items-stretch gap-1">
                  <div className="relative">
                    <Button
                      variant="secondary"
                      size="sm"
                      iconLeft="home"
                      loading={busyAction === 'operatory'}
                      onClick={() => {
                        setOpenMenu((m) => (m === 'operatory' ? null : 'operatory'));
                      }}
                    >
                      {dossier.operatory.replace(/\s*\(.*$/, '')}
                      <Icon kind="down" size={12} stroke={2} />
                    </Button>
                    {openMenu === 'operatory' ? (
                      <div className="absolute right-0 top-full z-20 mt-1 min-w-[160px] overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] py-1 shadow-[var(--sh-3)]">
                        {operatoryOptions.map((op) => (
                          <button
                            key={op}
                            type="button"
                            onClick={() => {
                              onChangeOperatory?.(op);
                              setOpenMenu(null);
                            }}
                            className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[13px] text-[var(--ink-1)] hover:bg-[var(--cream-1)]"
                          >
                            <Icon kind="home" size={14} stroke={2} />
                            {op}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <span className={cn(cardLabelClass, 'text-center text-[var(--ink-4)]')}>
                    Operatory
                  </span>
                </div>

                {/* Kiosk — resend action + current code below */}
                <div className="flex flex-col items-stretch gap-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft="send"
                    loading={busyAction === 'kiosk'}
                    onClick={onResendKioskCode}
                  >
                    Resend kiosk code
                  </Button>
                  <span className="text-center text-[11px] text-[var(--ink-3)]">
                    Code{' '}
                    <span className="font-[var(--font-mono)] font-semibold text-[var(--ink-1)]">
                      {dossier.kioskCode}
                    </span>
                  </span>
                </div>

                {/* Eligibility — check action + last verified below */}
                <div className="flex flex-col items-stretch gap-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft="shield-check"
                    loading={busyAction === 'eligibility'}
                    onClick={onCheckEligibility}
                  >
                    Check eligibility
                  </Button>
                  <span
                    className={cn(
                      'inline-flex items-center justify-center gap-1 text-center text-[11px]',
                      eligibilityStale ? 'text-[var(--gold)]' : 'text-[var(--ink-3)]',
                    )}
                  >
                    {(eligibilityLastChecked ?? verified) !== undefined ? (
                      <>
                        <Icon kind="clock" size={11} stroke={2} />
                        {eligibilityStale ? 'Stale · ' : 'Checked '}
                        {eligibilityLastChecked ?? verified}
                      </>
                    ) : (
                      'Not checked yet'
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Quick facts ──────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-7 border-b border-[var(--cream-2)] bg-[var(--cream-1)] px-6 py-4">
          <QuickFact icon="clock" label="Time" value={timeLabel} />
          <QuickFact icon="team" label="Provider" value={provName} />
          <QuickFact icon="home" label="Operatory" value={dossier.operatory} />
          <QuickFact icon="lock" label="Kiosk code" value={dossier.kioskCode} mono />
          <QuickFact
            icon="dollar"
            label="Production"
            value={`$${appt.production.toLocaleString()}`}
            hi
          />
        </div>

        {/* ── Body ─────────────────────────────────────────────────────── */}
        <div className="cal-scroll flex-1 overflow-auto">
          <div className="grid grid-cols-12 gap-4 p-6 pb-[26px]">
            {/* Opportunities (left) */}
            {ins.opportunities.length > 0 ? (
              <SectionCard
                icon="target"
                title="Opportunities"
                accent="var(--success)"
                className="col-span-5"
                bodyPad="p-0"
                right={<Pill tone="ai">{ins.opportunities.length} opportunities</Pill>}
              >
                <div className="flex flex-col">
                  {/* The Aligners card (amendment 30 §4) replaces the flat ortho-benefit
                      row for patients in the Invisalign agent. Mock: Orville Allison. */}
                  {appt.patient === 'Orville Allison' ? (
                    // For the Invisalign patient the Aligners card replaces the flat
                    // opportunity rows entirely (amendment 34 §1).
                    <div className="p-[13px]">
                      <InvAlignersCard stage="ready" invisalignHref="/patients/p001/invisalign" />
                    </div>
                  ) : (
                    ins.opportunities.map((o, i) => (
                      <OpportunityCard key={o.id} opportunity={o} first={i === 0} />
                    ))
                  )}
                </div>
              </SectionCard>
            ) : null}

            {/* Insurance estimate + family (right) */}
            {ins.estimate || ins.family.length > 0 ? (
              <div
                className={cn(
                  'flex flex-col gap-4',
                  ins.opportunities.length > 0 ? 'col-span-7' : 'col-span-12',
                )}
              >
                {ins.estimate ? (
                  <EstimateCard
                    estimate={ins.estimate}
                    verified={verified}
                    stale={eligibilityStale}
                  />
                ) : null}
                {ins.family.length > 0 ? <FamilyOverviewCard family={ins.family} /> : null}
              </div>
            ) : null}

            {/* Clinical alerts — safety context, moved to bottom (notes removed) */}
            <div className="col-span-12 overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-0)]">
              <div className="flex items-center gap-[11px] border-b border-[var(--cream-2)] bg-[var(--cream-1)] px-[17px] py-[13px]">
                <Chip icon="shield" accent="var(--terracotta)" />
                <span className="font-[var(--font-display)] text-[15.5px] font-semibold tracking-[-0.015em] text-[var(--ink-0)]">
                  Clinical alerts
                </span>
                <span className={cn(cardLabelClass, 'ml-1 text-[var(--ink-4)]')}>Always shown</span>
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(210px,1fr))] gap-[18px] p-[17px]">
                <AlertGroup
                  label="Allergies"
                  accent="var(--terracotta)"
                  critical
                  items={dossier.clinicalAlerts.allergies}
                />
                <AlertGroup
                  label="Conditions"
                  accent="var(--gold)"
                  items={dossier.clinicalAlerts.conditions}
                />
                <AlertGroup
                  label="Medications"
                  accent="var(--ink-3)"
                  items={dossier.clinicalAlerts.medications}
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ───────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3 border-t border-[var(--cream-2)] bg-[var(--cream-1)] px-6 py-3">
          <span className="inline-flex items-center gap-1.5 text-[11.5px] text-[var(--ink-3)]">
            <Icon kind="info" size={12} stroke={2} />
            Opportunities are based on insurance benefits and treatment history.
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              iconLeft="doc"
              onClick={() => {
                if (onPrintSummary) {
                  onPrintSummary();
                  return;
                }
                setPrintOpen(true);
              }}
            >
              Print Summary
            </Button>
          </div>
        </div>
      </div>
    </ViewportOverlay>

    {printOpen ? (
      <Modal
        open
        title="Print summary"
        onClose={() => {
          setPrintOpen(false);
        }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setPrintOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              iconLeft="doc"
              onClick={() => {
                setPrintOpen(false);
                printEstimateSummary({
                  patient: appt.patient,
                  demographics: dossier.demographics,
                  timeLabel,
                  provider: provName,
                  operatory: dossier.operatory,
                  verified,
                  estimate: ins.estimate,
                  opportunities: ins.opportunities,
                  alerts: dossier.clinicalAlerts,
                  include: { opportunities: inclOpportunities, alerts: inclAlerts },
                });
              }}
            >
              Print
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-[13px] leading-relaxed text-[var(--ink-2)]">
          The estimate, procedures and plan details are always included. Add any extra sections:
        </p>
        <div className="flex flex-col gap-3">
          <Checkbox
            label="Opportunities"
            checked={inclOpportunities}
            onChange={setInclOpportunities}
          />
          <Checkbox
            label="Clinical alerts (allergies, conditions, medications)"
            checked={inclAlerts}
            onChange={setInclAlerts}
          />
        </div>
      </Modal>
    ) : null}
    </>
  );
}

/* ─────────────────────────────── Sub-components ─────────────────────────── */

function QuickFact({
  icon,
  label,
  value,
  mono,
  hi,
}: {
  icon: IconKind;
  label: string;
  value: string;
  mono?: boolean;
  hi?: boolean;
}) {
  return (
    <div className="flex items-center gap-[11px]">
      <Chip icon={icon} accent={hi ? 'var(--success)' : 'var(--ink-3)'} size={32} />
      <div className="min-w-0">
        <div className={cn(cardLabelClass, 'text-[var(--ink-4)]')}>{label}</div>
        <div
          className={cn(
            'mt-0.5 tracking-[-0.01em] whitespace-nowrap',
            mono ? 'font-[var(--font-mono)]' : 'font-[var(--font-body)]',
            hi
              ? 'text-base font-bold text-[var(--success)]'
              : 'text-sm font-semibold text-[var(--ink-0)]',
          )}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

function AlertGroup({
  label,
  items,
  accent,
  critical,
}: {
  label: string;
  items: string[];
  accent: string;
  critical?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-center gap-1.5">
        <Icon kind="alert" size={13} stroke={2} color={accent} />
        <span className={cn(cardLabelClass, 'text-[var(--ink-3)]')}>{label}</span>
        <span className="font-[var(--font-mono)] text-[10px] font-bold text-[var(--ink-4)]">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <span className="text-[11px] text-[var(--ink-4)]">None on file</span>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((it) => (
            <span
              key={it}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[var(--r-pill)] border bg-[var(--cream-1)] px-[11px] py-1 text-[12.5px] font-semibold text-[var(--ink-1)]"
              style={{
                borderColor: critical
                  ? `color-mix(in srgb, ${accent} 38%, var(--cream-2))`
                  : 'var(--cream-2)',
              }}
            >
              {critical ? (
                <span
                  className="h-[5px] w-[5px] shrink-0 rounded-full"
                  style={{ background: accent }}
                />
              ) : null}
              {it}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function PriorityBadge({ priority }: { priority: OpportunityPriority }) {
  const map: Record<OpportunityPriority, [string, string]> = {
    High: ['var(--success)', 'var(--success-tint)'],
    Medium: ['var(--gold)', 'var(--gold-tint)'],
    Low: ['var(--ink-3)', 'var(--cream-2)'],
  };
  const [fg, bg] = map[priority];
  return (
    <span
      className="inline-flex items-center rounded-[5px] px-[7px] py-0.5 text-[10px] font-bold"
      style={{ color: fg, background: bg }}
    >
      {priority}
    </span>
  );
}

function OpportunityCard({
  opportunity: o,
  first,
}: {
  opportunity: OpportunityItem;
  first: boolean;
}) {
  return (
    <div
      className={cn(
        'flex gap-3 px-[17px] py-4',
        !first && 'border-t border-[var(--cream-2)]',
        o.highlighted &&
          'border-l-[3px] border-l-[var(--ai-3)] bg-[color-mix(in_srgb,var(--ai-3)_6%,transparent)]',
      )}
    >
      <Chip icon={o.icon} accent={o.accent} size={38} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[14px] font-semibold tracking-[-0.01em] text-[var(--ink-0)]">
            {o.title}
          </span>
          {o.priority ? <PriorityBadge priority={o.priority} /> : null}
        </div>
        {o.description ? (
          <div className="mt-1 text-[12.5px] leading-snug text-[var(--ink-2)]">{o.description}</div>
        ) : null}
        {o.detail ? (
          <div className="mt-0.5 text-[11.5px] text-[var(--ink-3)]">{o.detail}</div>
        ) : null}
      </div>
      {o.estValue ? (
        <div className="flex w-[150px] shrink-0 flex-col items-end text-right">
          {o.estLabel ? (
            <div className={cn(cardLabelClass, 'text-[var(--ink-4)]')}>{o.estLabel}</div>
          ) : null}
          <div
            className="mt-0.5 font-[var(--font-display)] text-[17px] font-bold tracking-[-0.02em]"
            style={{ color: EST_TONE[o.estTone ?? 'ink'] }}
          >
            {o.estValue}
          </div>
          {o.estNote ? <div className="text-[10.5px] text-[var(--ink-4)]">{o.estNote}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

function EstimateFigure({
  label,
  value,
  tone = 'var(--ink-0)',
  big,
  sub,
}: {
  label: string;
  value: string;
  tone?: string;
  big?: boolean;
  sub?: string;
}) {
  return (
    <div className="min-w-0">
      <div className={cn(cardLabelClass, 'text-[var(--ink-4)]')}>{label}</div>
      <div
        className={cn(
          'mt-0.5 font-[var(--font-display)] font-bold tracking-[-0.03em]',
          big ? 'text-[34px] leading-none' : 'text-[24px] leading-none',
        )}
        style={{ color: tone }}
      >
        {value}
      </div>
      {sub ? <div className="mt-1 text-[11px] text-[var(--ink-3)]">{sub}</div> : null}
    </div>
  );
}

function PlanTile({
  label,
  value,
  sub,
  subTone,
  met,
}: {
  label: string;
  value: string;
  sub?: string;
  subTone?: string;
  met?: boolean;
}) {
  return (
    <div className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3 py-2.5">
      <div className={cn(cardLabelClass, 'text-[var(--ink-4)]')}>{label}</div>
      <div className="mt-1 font-[var(--font-display)] text-[16px] font-bold tracking-[-0.02em] text-[var(--ink-0)]">
        {value}
      </div>
      {sub ? (
        <div
          className="mt-0.5 inline-flex items-center gap-1 text-[11px]"
          style={{ color: subTone ?? 'var(--ink-3)' }}
        >
          {met ? <Icon kind="check-circ" size={11} stroke={2} /> : null}
          {sub}
        </div>
      ) : null}
    </div>
  );
}

function EstimateCard({
  estimate,
  verified,
  stale,
}: {
  estimate: InsuranceEstimate;
  verified?: string | undefined;
  stale?: boolean | undefined;
}) {
  const { plan } = estimate;
  const hasBreakdown =
    estimate.officeFee !== undefined &&
    estimate.insuranceEst !== undefined &&
    estimate.patientEst !== undefined;
  return (
    <div
      className="overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] shadow-[var(--sh-1)]"
      style={{ background: 'var(--cream-1)', borderTopWidth: 3, borderTopColor: 'var(--ai-3)' }}
    >
      <div className="flex flex-wrap items-center gap-2 px-[18px] pb-3 pt-4">
        <Icon kind="shield-check" size={14} stroke={2} color="var(--ai-3)" />
        <span className={cn(cardLabelClass)} style={{ color: 'var(--ai-3)' }}>
          Today’s Insurance Estimate
        </span>
        <span className="text-[12.5px] font-semibold text-[var(--ink-1)]">{estimate.carrier}</span>
        {verified ? (
          <span
            className="ml-auto inline-flex items-center gap-1 rounded-[var(--r-pill)] px-2 py-0.5 text-[11px] font-semibold"
            style={{
              color: stale ? 'var(--gold)' : 'var(--success)',
              background: stale ? 'var(--gold-tint)' : 'var(--success-tint)',
            }}
          >
            <Icon kind="check" size={11} stroke={2.4} />
            Eligibility verified {verified}
          </span>
        ) : null}
      </div>

      <div className="px-[18px] pb-[18px]">
        {/* Hero: patient today + fee breakdown */}
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3 rounded-[var(--r-md)] bg-[var(--ai-tint)] px-4 py-3.5">
          <EstimateFigure
            label="Patient Estimated Today"
            value={money(estimate.patientToday)}
            tone="var(--ai-3)"
            big
            sub={estimate.patientNote ?? 'You pay today (est.)'}
          />
          {hasBreakdown ? (
          <div className="flex items-end gap-3">
            <EstimateFigure label="Office Fee" value={money(estimate.officeFee ?? 0)} />
            <span className="pb-1 text-[22px] font-bold text-[var(--ink-4)]">−</span>
            <EstimateFigure
              label="Insurance Est."
              value={money(estimate.insuranceEst ?? 0)}
              tone="var(--success)"
            />
            <span className="pb-1 text-[22px] font-bold text-[var(--ink-4)]">=</span>
            <EstimateFigure
              label="Patient Est."
              value={money(estimate.patientEst ?? 0)}
              tone="var(--ai-3)"
            />
          </div>
          ) : null}
        </div>

        {estimate.procedures.length > 0 ? (
        <div className="mt-3.5">
          <div className={cn(cardLabelClass, 'mb-1.5 text-[var(--ink-3)]')}>Today’s procedures</div>
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="text-[var(--ink-4)]">
                <th className="py-1 text-left font-medium" />
                <th className="py-1 pl-2 text-right font-medium">Office Fee</th>
                <th className="py-1 pl-2 text-right font-medium">Insurance Est.</th>
                <th className="py-1 pl-2 text-right font-medium">Patient Est.</th>
              </tr>
            </thead>
            <tbody>
              {estimate.procedures.map((p) => (
                <tr key={p.code} className="border-t border-[var(--cream-2)]">
                  <td className="py-1.5 pr-2">
                    <span className="font-[var(--font-mono)] font-semibold text-[var(--ink-1)]">
                      {p.code}
                    </span>{' '}
                    <span className="text-[var(--ink-2)]">{p.description}</span>
                  </td>
                  <td className="py-1.5 pl-2 text-right font-[var(--font-mono)] text-[var(--ink-1)]">
                    {money(p.officeFee)}
                  </td>
                  <td className="py-1.5 pl-2 text-right font-[var(--font-mono)] text-[var(--success)]">
                    {p.insuranceEst > 0 ? `−${money(p.insuranceEst)}` : money(0)}
                  </td>
                  <td className="py-1.5 pl-2 text-right font-[var(--font-mono)] font-semibold text-[var(--ink-0)]">
                    {money(p.patientEst)}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-[var(--cream-3)]">
                <td className="py-1.5 pr-2 font-semibold text-[var(--ink-0)]">Totals</td>
                <td className="py-1.5 pl-2 text-right font-[var(--font-mono)] font-semibold text-[var(--ink-0)]">
                  {money(estimate.officeFee ?? 0)}
                </td>
                <td className="py-1.5 pl-2 text-right font-[var(--font-mono)] font-semibold text-[var(--success)]">
                  −{money(estimate.insuranceEst ?? 0)}
                </td>
                <td className="py-1.5 pl-2 text-right font-[var(--font-mono)] font-bold text-[var(--ai-3)]">
                  {money(estimate.patientEst ?? 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        ) : null}

        {plan ? (
        <div className="mt-3.5 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <PlanTile
            label="Annual Max"
            value={money(plan.annualMax)}
            sub={`${money(plan.annualRemaining)} remaining`}
          />
          <PlanTile
            label="Deductible"
            value={money(plan.deductible)}
            sub={plan.deductibleMet ? 'Met' : 'Not met'}
            subTone={plan.deductibleMet ? 'var(--success)' : 'var(--ink-3)'}
            met={plan.deductibleMet}
          />
          <PlanTile
            label="Ortho Lifetime Max"
            value={money(plan.orthoMax)}
            sub={`${money(plan.orthoRemaining)} remaining`}
          />
          <PlanTile label="Plan Type" value={plan.planType} />
          <PlanTile
            label="Effective"
            value={plan.effectiveFrom}
            sub={`to ${plan.effectiveTo}`}
          />
        </div>
        ) : null}

        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-[var(--ink-4)]">
          <Icon kind="info" size={11} stroke={2} />
          Estimates are not a guarantee of payment. Final insurance payment may vary.
        </div>
      </div>
    </div>
  );
}

function FamilyBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, Math.round((value / max) * 100))) : 0;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--cream-2)]">
      <div className="h-full rounded-full bg-[var(--success)]" style={{ width: `${String(pct)}%` }} />
    </div>
  );
}

function FamilyMemberRow({ member: m }: { member: FamilyOverviewMember }) {
  const overdue = m.recallStatus === 'overdue';
  return (
    <tr className="border-t border-[var(--cream-2)] align-top">
      <td className="py-2.5 pr-2">
        <div
          className={cn(
            'text-[13px] font-semibold',
            m.isSelf ? 'text-[var(--ai-3)]' : 'text-[var(--ink-0)]',
          )}
        >
          {m.name}
        </div>
        <div className="text-[11px] text-[var(--ink-4)]">{m.relation}</div>
      </td>
      <td className="py-2.5 pr-2">
        <div className="text-[12.5px] text-[var(--ink-1)]">{m.recallLabel}</div>
        <div
          className="text-[11px] font-semibold"
          style={{ color: overdue ? 'var(--terracotta)' : 'var(--success)' }}
        >
          {m.recallNote}
        </div>
      </td>
      <td className="py-2.5 pr-2">
        <div className="font-[var(--font-mono)] text-[12.5px] font-semibold text-[var(--ink-0)]">
          {m.unscheduledTreatment ? money(m.unscheduledTreatment) : '—'}
        </div>
        {m.unscheduledItems ? (
          <div className="text-[11px] text-[var(--ink-4)]">
            {m.unscheduledItems} item{m.unscheduledItems > 1 ? 's' : ''}
          </div>
        ) : null}
      </td>
      <td className="py-2.5">
        {m.benefitsRemaining !== undefined ? (
          <>
            <div className="mb-1 font-[var(--font-mono)] text-[12.5px] font-semibold text-[var(--success)]">
              {money(m.benefitsRemaining)}
            </div>
            <FamilyBar value={m.benefitsRemaining} max={m.benefitsMax ?? 0} />
          </>
        ) : (
          <div className="font-[var(--font-mono)] text-[12.5px] text-[var(--ink-4)]">—</div>
        )}
      </td>
    </tr>
  );
}

function FamilyOverviewCard({ family }: { family: FamilyOverviewMember[] }) {
  return (
    <SectionCard
      icon="team"
      title={`Family Overview (${String(family.length)} members)`}
      accent="var(--ai-4)"
    >
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-left text-[var(--ink-4)]">
            <th className={cn(cardLabelClass, 'py-1 pr-2 font-bold')}>Member</th>
            <th className={cn(cardLabelClass, 'py-1 pr-2 font-bold')}>Recall Status</th>
            <th className={cn(cardLabelClass, 'py-1 pr-2 font-bold')}>Unscheduled Treatment</th>
            <th className={cn(cardLabelClass, 'py-1 font-bold')}>Benefits Remaining</th>
          </tr>
        </thead>
        <tbody>
          {family.map((m) => (
            <FamilyMemberRow key={m.id} member={m} />
          ))}
        </tbody>
      </table>
    </SectionCard>
  );
}
